import type {
  AccessLevel,
  AddShareInput,
  AdminSettings,
  AppConfig,
  DirectoryEntry,
  DrawingGroup,
  DrawingSummary,
  EnvironmentType,
  PublicUser,
  Role,
  ShareEntry,
  ShareSubjectType,
  TrashedDrawingSummary,
  UserGroupMemberEntry,
  UserGroupRole,
  UserGroupSummary,
} from '@system-atlas/shared';
import { shouldRetryCsrfError } from '@system-atlas/shared';

export interface LockStatus {
  lockedByName: string;
  isMine: boolean;
  expiresAt: string;
}

export interface ImageMeta {
  id: string;
  name: string;
  mime: string;
  width: number | null;
  height: number | null;
  sizeBytes: number;
  isLibrary: boolean;
  categoryId: string | null;
  environmentType: EnvironmentType;
  sortOrder: number;
  uploadedBy: string | null;
  createdAt: string;
}

export interface ImageCategory {
  id: string;
  name: string;
  parentId: string | null;
  environmentType: EnvironmentType;
  sortOrder: number;
}

export type ImageScope = 'library' | 'mine' | 'all' | 'uploads';

export interface DrawingDetail {
  id: string;
  name: string;
  description: string | null;
  groupId: string | null;
  content: unknown;
  createdAt: string;
  updatedAt: string;
  access: AccessLevel;
}

export interface ListDrawingsParams {
  search?: string;
  groupId?: string;
  includeSubgroups?: boolean;
  scope?: 'all' | 'own' | 'shared';
  sort?: 'updated' | 'created' | 'name';
}

export interface UserRow {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  isActive: boolean;
  mustChangePassword: boolean;
  totpEnabled: boolean;
  mfaRequired: boolean;
  createdAt: string;
}

/** Login-Ergebnis: entweder fertig angemeldet oder zweiter MFA-Schritt nötig. */
export type LoginResult = { user: PublicUser } | { mfaRequired: true };

export class ApiError extends Error {
  status: number;
  code?: string;
  issues?: string[];
  constructor(status: number, message: string, code?: string, issues?: string[]) {
    super(message);
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

let csrfToken: string | null = null;

async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch('/api/csrf-token', { credentials: 'same-origin' });
  const data = (await res.json()) as { csrfToken: string };
  csrfToken = data.csrfToken;
  return csrfToken;
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  retry = false,
  opts?: { keepalive?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {};
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  // keepalive lässt den Request ein Entladen der Seite überleben (Flush-Save).
  // Browser begrenzen keepalive-Bodies auf ~64 KB – größere Szenen deckt der Debounce-Autosave ab.
  if (opts?.keepalive) init.keepalive = true;
  if (method !== 'GET') {
    headers['x-csrf-token'] = await getCsrfToken();
  }

  const res = await fetch(url, init);
  const data = res.status === 204 ? {} : await res.json().catch(() => ({}));

  if (!res.ok) {
    const payload = data as { error?: string; code?: string; issues?: string[] };
    // Nur bei veraltetem CSRF-Token einmal neu holen und erneut versuchen –
    // nicht bei echten Berechtigungs-403 (sonst doppelte Requests auf Limits).
    if (shouldRetryCsrfError(res.status, payload.code, method, retry)) {
      csrfToken = null;
      return request<T>(method, url, body, true, opts);
    }
    throw new ApiError(res.status, payload.error ?? 'Es ist ein Fehler aufgetreten.', payload.code, payload.issues);
  }
  return data as T;
}

/** Multipart-POST (Datei-Upload) mit CSRF-Header, ohne content-type zu setzen. */
async function requestForm<T>(url: string, formData: FormData): Promise<T> {
  const headers: Record<string, string> = { 'x-csrf-token': await getCsrfToken() };
  const res = await fetch(url, { method: 'POST', credentials: 'same-origin', headers, body: formData });
  const data = res.status === 204 ? {} : await res.json().catch(() => ({}));
  if (!res.ok) {
    const payload = data as { error?: string; code?: string };
    // CSRF-Cache nur bei echtem CSRF-Fehler verwerfen, nicht bei Berechtigungs-403.
    if (shouldRetryCsrfError(res.status, payload.code, 'POST', false)) csrfToken = null;
    throw new ApiError(res.status, payload.error ?? 'Es ist ein Fehler aufgetreten.', payload.code);
  }
  return data as T;
}

/** Nach Login/Logout den CSRF-Cache leeren (Session wurde neu erstellt). */
function resetCsrf(): void {
  csrfToken = null;
}

export const api = {
  getAppConfig: () => request<AppConfig>('GET', '/api/app-config'),
  me: () => request<{ user: PublicUser }>('GET', '/api/auth/me'),
  login: async (email: string, password: string) => {
    const result = await request<LoginResult>('POST', '/api/auth/login', { email, password });
    resetCsrf();
    return result;
  },
  verifyMfa: async (code: string) => {
    const result = await request<{ user: PublicUser }>('POST', '/api/auth/mfa/verify', { code });
    resetCsrf();
    return result;
  },
  mfaSetup: () => request<{ secret: string; otpauthUrl: string }>('POST', '/api/auth/mfa/setup'),
  mfaEnable: (code: string) => request<{ recoveryCodes: string[] }>('POST', '/api/auth/mfa/enable', { code }),
  mfaDisable: (password: string) => request<{ ok: true }>('POST', '/api/auth/mfa/disable', { password }),
  logout: async () => {
    const result = await request<{ ok: true }>('POST', '/api/auth/logout');
    resetCsrf();
    return result;
  },
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('POST', '/api/auth/change-password', { currentPassword, newPassword }),
  listUsers: () => request<{ users: UserRow[] }>('GET', '/api/users'),
  createUser: (input: { email: string; displayName: string; role: Role; password?: string }) =>
    request<{ user: PublicUser; tempPassword?: string }>('POST', '/api/users', input),
  updateUser: (id: string, input: { displayName?: string; email?: string }) =>
    request<{ ok: true }>('PATCH', `/api/users/${id}`, input),
  setUserRole: (id: string, role: Role) =>
    request<{ ok: true }>('PATCH', `/api/users/${id}/role`, { role }),
  setUserStatus: (id: string, isActive: boolean) =>
    request<{ ok: true }>('PATCH', `/api/users/${id}/status`, { isActive }),
  resetUserPassword: (id: string) =>
    request<{ tempPassword: string }>('POST', `/api/users/${id}/reset-password`),
  resetUserMfa: (id: string) => request<{ ok: true }>('POST', `/api/users/${id}/mfa/reset`),
  setUserMfaRequired: (id: string, mfaRequired: boolean) =>
    request<{ ok: true }>('PATCH', `/api/users/${id}/mfa-required`, { mfaRequired }),
  deleteUser: (id: string, transferToUserId: string) =>
    request<{ ok: true }>('DELETE', `/api/users/${id}`, { transferToUserId }),

  // --- Globale Einstellungen (nur Superadmin) -----------------------------
  getAdminSettings: () => request<AdminSettings>('GET', '/api/admin/settings'),
  setMfaGlobalRequired: (mfaGlobalRequired: boolean) =>
    request<AdminSettings>('PATCH', '/api/admin/settings/mfa-global-required', { mfaGlobalRequired }),
  setScimSettings: (input: { scimDeprovisionAction: 'deactivate' | 'delete'; scimFallbackOwnerId: string | null }) =>
    request<AdminSettings>('PATCH', '/api/admin/settings/scim', input),

  // --- Zeichnungen --------------------------------------------------------
  listDrawings: (params: ListDrawingsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.groupId) qs.set('groupId', params.groupId);
    if (params.includeSubgroups) qs.set('includeSubgroups', 'true');
    if (params.scope) qs.set('scope', params.scope);
    if (params.sort) qs.set('sort', params.sort);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ drawings: DrawingSummary[] }>('GET', `/api/drawings${suffix}`);
  },
  getDrawing: (id: string) => request<{ drawing: DrawingDetail }>('GET', `/api/drawings/${id}`),
  createDrawing: (input: { name: string; description?: string; groupId?: string | null }) =>
    request<{ drawing: DrawingSummary }>('POST', '/api/drawings', input),
  importDrawing: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return requestForm<{ drawing: DrawingSummary }>('/api/drawings/import', form);
  },
  updateDrawing: (id: string, input: { name?: string; description?: string | null; groupId?: string | null }) =>
    request<{ ok: true }>('PATCH', `/api/drawings/${id}`, input),
  saveDrawingContent: (id: string, scene: unknown, opts?: { keepalive?: boolean }) =>
    request<{ ok: true }>('PUT', `/api/drawings/${id}/content`, scene, false, opts),
  deleteDrawing: (id: string) => request<{ ok: true }>('DELETE', `/api/drawings/${id}`),
  listTrash: () => request<{ drawings: TrashedDrawingSummary[] }>('GET', '/api/drawings/trash'),
  restoreDrawing: (id: string) => request<{ ok: true }>('POST', `/api/drawings/${id}/restore`),
  purgeDrawing: (id: string) => request<{ ok: true }>('DELETE', `/api/drawings/${id}/permanent`),

  // --- Gruppen ------------------------------------------------------------
  listGroups: () => request<{ groups: DrawingGroup[] }>('GET', '/api/drawing-groups'),
  createGroup: (input: { name: string; parentId?: string | null }) =>
    request<{ group: DrawingGroup }>('POST', '/api/drawing-groups', input),
  updateGroup: (id: string, input: { name?: string; parentId?: string | null }) =>
    request<{ ok: true }>('PATCH', `/api/drawing-groups/${id}`, input),
  deleteGroup: (id: string) => request<{ ok: true }>('DELETE', `/api/drawing-groups/${id}`),

  // --- Bilder -------------------------------------------------------------
  listImages: (params: { scope?: ImageScope; categoryId?: string; search?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.scope) qs.set('scope', params.scope);
    if (params.categoryId) qs.set('categoryId', params.categoryId);
    if (params.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ images: ImageMeta[] }>('GET', `/api/images${suffix}`);
  },
  uploadImage: (file: File, meta: { name: string; environmentType?: EnvironmentType; categoryId?: string }) => {
    const form = new FormData();
    form.append('name', meta.name);
    if (meta.environmentType) form.append('environmentType', meta.environmentType);
    if (meta.categoryId) form.append('categoryId', meta.categoryId);
    form.append('file', file);
    return requestForm<{ image: ImageMeta }>('/api/images', form);
  },
  updateImage: (id: string, input: { name?: string; categoryId?: string | null; environmentType?: EnvironmentType }) =>
    request<{ ok: true }>('PATCH', `/api/images/${id}`, input),
  deleteImage: (id: string) => request<{ ok: true }>('DELETE', `/api/images/${id}`),
  promoteImage: (id: string, input: { categoryId?: string | null; environmentType?: EnvironmentType } = {}) =>
    request<{ ok: true }>('POST', `/api/images/${id}/promote`, input),
  reorderImages: (items: { id: string; sortOrder: number }[]) =>
    request<{ ok: true }>('PATCH', '/api/images/reorder', { items }),
  imageRawUrl: (id: string) => `/api/images/${id}/raw`,

  // --- Bild-Kategorien ----------------------------------------------------
  listImageCategories: () => request<{ categories: ImageCategory[] }>('GET', '/api/image-categories'),
  createImageCategory: (input: { name: string; parentId?: string | null; environmentType?: EnvironmentType }) =>
    request<{ category: ImageCategory }>('POST', '/api/image-categories', input),
  reorderImageCategories: (items: { id: string; sortOrder: number }[]) =>
    request<{ ok: true }>('PATCH', '/api/image-categories/reorder', { items }),
  deleteImageCategory: (id: string) => request<{ ok: true }>('DELETE', `/api/image-categories/${id}`),

  // --- Freigaben (generalisiert) ------------------------------------------
  listShares: (subjectType: ShareSubjectType, subjectId: string) => {
    const qs = new URLSearchParams({ subjectType, subjectId });
    return request<{ shares: ShareEntry[] }>('GET', `/api/shares?${qs.toString()}`);
  },
  addShare: (input: AddShareInput) => request<{ ok: true }>('POST', '/api/shares', input),
  removeShare: (shareId: string) => request<{ ok: true }>('DELETE', `/api/shares/${shareId}`),

  // --- Verzeichnis --------------------------------------------------------
  searchDirectory: (q: string, type: 'user' | 'user_group' | 'all' = 'all') => {
    const qs = new URLSearchParams({ type });
    if (q) qs.set('q', q);
    return request<{ entries: DirectoryEntry[] }>('GET', `/api/directory?${qs.toString()}`);
  },

  // --- Benutzergruppen ----------------------------------------------------
  listUserGroups: () => request<{ groups: UserGroupSummary[] }>('GET', '/api/user-groups'),
  createUserGroup: (input: { name: string; description?: string | null }) =>
    request<{ id: string }>('POST', '/api/user-groups', input),
  updateUserGroup: (id: string, input: { name?: string; description?: string | null }) =>
    request<{ ok: true }>('PATCH', `/api/user-groups/${id}`, input),
  deleteUserGroup: (id: string) => request<{ ok: true }>('DELETE', `/api/user-groups/${id}`),
  listUserGroupMembers: (id: string) =>
    request<{ members: UserGroupMemberEntry[] }>('GET', `/api/user-groups/${id}/members`),
  addUserGroupMember: (id: string, input: { userId: string; role?: UserGroupRole }) =>
    request<{ ok: true }>('POST', `/api/user-groups/${id}/members`, input),
  removeUserGroupMember: (id: string, userId: string) =>
    request<{ ok: true }>('DELETE', `/api/user-groups/${id}/members/${userId}`),
  setUserGroupMemberRole: (id: string, userId: string, role: UserGroupRole) =>
    request<{ ok: true }>('PATCH', `/api/user-groups/${id}/members/${userId}/role`, { role }),

  // --- Sperren ------------------------------------------------------------
  getLock: (drawingId: string) => request<{ lock: LockStatus | null }>('GET', `/api/drawings/${drawingId}/lock`),
  acquireLock: (drawingId: string) =>
    request<{ granted: true; expiresAt: string }>('POST', `/api/drawings/${drawingId}/lock`),
  heartbeatLock: (drawingId: string) => request<{ ok: true }>('POST', `/api/drawings/${drawingId}/lock/heartbeat`),
  releaseLock: (drawingId: string) => request<{ ok: true }>('POST', `/api/drawings/${drawingId}/lock/release`),
  forceReleaseLock: (drawingId: string) => request<{ ok: true }>('DELETE', `/api/drawings/${drawingId}/lock`),
};
